import { InputAdornment, makeStyles } from '@material-ui/core'
import { t } from 'i18next'
import React, { useEffect } from 'react'
import { Control, DeepMap, FieldError } from 'react-hook-form'
import { EmissionType } from '../../generated/graphql'
import {
  convertKgsToTons,
  convertTonsToKgs,
  emissionPerAreaUnit,
  emissionPerYearUnit,
  largeEmissionUnit,
  parseLocaleDecimalToFloat,
} from '../../util/formatters'
import { validators } from '../../validation/validators'
import { ControlledTextField } from '../form/ControlledTextField'
import { FormGroup } from '../form/FormGroup'
import {
  calculateInherentValueByEmissionType,
  calculateValueByEmissionType,
  isLargeUnitType,
} from './emission-util'
import type { FormValues } from './EmissionDialog'

const useStyles = makeStyles((theme) => ({
  row: {
    display: 'flex',
    gap: theme.spacing(1),
  },
}))

type Props = {
  control: Control<FormValues>
  emissionType: EmissionType
  errors: DeepMap<FormValues, FieldError>
  disabled: boolean
  area: number
  inspectionLengthInYears: number
  setValue: (
    name: keyof FormValues,
    value: unknown,
    config?:
      | Partial<{
          shouldValidate: boolean
          shouldDirty: boolean
        }>
      | undefined,
  ) => void
  prefillValue?: string
}

export default function ControlledEmissionValue({
  control,
  emissionType,
  errors,
  disabled,
  area,
  inspectionLengthInYears,
  setValue,
  prefillValue,
}: Props): React.ReactElement {
  const classes = useStyles()

  useEffect(() => {
    if (prefillValue && area) {
      const parsedValue = parseLocaleDecimalToFloat(prefillValue)
      const value = isLargeUnitType(emissionType)
        ? convertTonsToKgs(parsedValue)
        : parsedValue
      const calculationInput = {
        emissionType,
        area,
        inspectionLengthInYears,
        value,
      }
      const calculatedInherentValue =
        calculateInherentValueByEmissionType(calculationInput)
      setValue('inherentValue', calculatedInherentValue.toFixed(1))
    }
  }, [prefillValue, area, emissionType, inspectionLengthInYears, setValue])

  const onValueChange = (formValue: string) => {
    const parsedValue = parseLocaleDecimalToFloat(formValue)
    const value = isLargeUnitType(emissionType)
      ? convertTonsToKgs(parsedValue)
      : parsedValue
    const calculationInput = {
      emissionType,
      area,
      inspectionLengthInYears,
      value,
    }
    const calculatedInherentValue =
      calculateInherentValueByEmissionType(calculationInput)
    setValue('inherentValue', calculatedInherentValue.toFixed(1))
  }

  const onInherentValueChange = (inherentValue: string) => {
    const calculationInput = {
      emissionType,
      area,
      inspectionLengthInYears,
      inherentValue: parseLocaleDecimalToFloat(inherentValue),
    }
    const calculatedValue = calculateValueByEmissionType(calculationInput)
    const value = isLargeUnitType(emissionType)
      ? convertKgsToTons(calculatedValue)
      : calculatedValue
    setValue('value', value.toFixed(1))
  }

  return (
    <FormGroup className={classes.row}>
      <ControlledTextField
        control={control}
        name="value"
        type="text"
        label={t('carbonFootprint.dialog.value')}
        error={errors.value}
        disabled={disabled}
        onChange={(event) => onValueChange(event.target.value)}
        rules={{
          ...validators.decimal,
          ...validators.required,
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {isLargeUnitType(emissionType)
                ? largeEmissionUnit
                : emissionPerYearUnit}
            </InputAdornment>
          ),
        }}
      />
      <ControlledTextField
        control={control}
        name="inherentValue"
        type="text"
        label={t('carbonFootprint.dialog.inherentValue')}
        onChange={(event) => onInherentValueChange(event.target.value)}
        error={errors.inherentValue}
        disabled={disabled}
        rules={{
          ...validators.decimal,
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {emissionPerAreaUnit}
            </InputAdornment>
          ),
        }}
      />
    </FormGroup>
  )
}
